import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from 'shared/lib/cn';

export type MoneyFieldProps = {
  value: string;
  onChange: (raw: string) => void;
  onBlur?: () => void;
  step?: number;
  /** Set it, and the enclosing Field's `htmlFor`, so clicking the label focuses
   *  the input. `ariaLabel` still names the input and the two steppers, so pass
   *  the label's own visible text as `ariaLabel` to keep the accessible name
   *  containing it (WCAG 2.5.3 Label in Name). */
  id?: string;
  ariaLabel: string;
  disabled?: boolean;
  prefix?: string;
  className?: string;
};

/** Money entry with steppers. Like NumField it never clamps a keystroke; the
 *  steppers only act when the current value already parses as a number. */
export function MoneyField({ value, onChange, onBlur, step = 1, id, ariaLabel, disabled, prefix = '$', className }: MoneyFieldProps) {
  const nudge = (direction: 1 | -1) => {
    const n = parseFloat(value);
    if (isNaN(n)) return;
    onChange(String(Number((n + direction * step).toFixed(2))));
  };

  return (
    <div className={cn('flex items-stretch gap-1', className)}>
      <div className="flex flex-1 items-center rounded-[var(--radius-2)] border border-hairline/30 bg-surface-overlay px-2 focus-within:ring-2 focus-within:ring-brand/60">
        <span className="font-code text-xs text-ink-muted">{prefix}</span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          aria-label={ariaLabel}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className="h-9 w-full bg-transparent px-2 font-code text-sm text-ink focus:outline-none disabled:opacity-50"
        />
      </div>
      <div className="flex flex-col">
        <button type="button" aria-label={`Increase ${ariaLabel}`} disabled={disabled} onClick={() => nudge(1)}
          className="flex h-[18px] w-6 items-center justify-center rounded-t-[var(--radius-2)] border border-hairline/25 text-ink-muted hover:bg-surface-high disabled:opacity-50">
          <ChevronUp className="h-3 w-3" />
        </button>
        <button type="button" aria-label={`Decrease ${ariaLabel}`} disabled={disabled} onClick={() => nudge(-1)}
          className="flex h-[18px] w-6 items-center justify-center rounded-b-[var(--radius-2)] border border-t-0 border-hairline/25 text-ink-muted hover:bg-surface-high disabled:opacity-50">
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
